import { appTheme } from "@/app-theme";
import { useAppSelector } from "@/store/store-hooks";
import { selectThemeMode } from "@/store/theme/theme-slice";
import { ThemeProvider, useColorScheme } from "@mui/material";
import {
  useLayoutEffect,
  type JSX,
  type PropsWithChildren,
  type ReactNode,
} from "react";

/**
 * Applies the effective theme mode to MUI's color scheme; must be a child of the ThemeProvider whose context it reads
 *
 * @returns the children unchanged
 */
function ColorSchemeSync({ children }: PropsWithChildren): ReactNode {
  const mode = useAppSelector(selectThemeMode);
  const { setMode } = useColorScheme();

  // Layout effect so a dark OS never flashes a dark first frame when the mode resolves to light
  useLayoutEffect(() => {
    setMode(mode);
  }, [mode, setMode]);

  return children;
}

/**
 * Same setup as the Dashboard: one theme with light and dark color schemes, light by default.
 * `storageManager={null}` keeps MUI from persisting the mode, the `theme_mode` setting is the source of truth.
 *
 * @returns a MUI theme provider switching the app theme's color scheme
 */
export function DynamicThemeProvider({
  children,
}: PropsWithChildren): JSX.Element {
  return (
    <ThemeProvider theme={appTheme} defaultMode="light" storageManager={null}>
      <ColorSchemeSync>{children}</ColorSchemeSync>
    </ThemeProvider>
  );
}
